"use client";
import { usePathname } from "next/navigation";

import { ArrowUpOnSquareIcon } from "@heroicons/react/24/outline";
import { FacebookShareButton, PinterestShareButton } from "react-share";

import { ClickServer } from "shared-lib/clickserver";
import * as gtag from "shared-lib/gtag";
import { brand } from "shared-lib/helpers/envFunctions";
import { log } from "shared-lib/helpers/logging/logger";
import {
  TRACKING_EVENTS,
  TRACKING_CATEGORIES,
  logStatsig,
} from "shared-lib/helpers/statsigFunctions";

import FacebookIconSocial from "shared-components/components/icons/social/Facebook";
import PinterestIconSocial from "shared-components/components/icons/social/Pinterest";

import RecipeDownload from "@/components/recipe/download";
import RecipePrint from "@/components/recipe/print";
import {
  getFrontendSharingURL_App,
  getRecipeImageURL,
} from "@/lib/helpers/backendFunctions";
import { getTitle } from "@/lib/helpers/brandFunctions";
import { getPlaintextDescription } from "@/lib/helpers/recipeFunctions";

export default function RecipeShareActions({ recipe }: { recipe: Recipe }) {
  const pathName = usePathname();
  return (
    <>
      {/* Icon group */}
      <div className="flex items-center gap-8 print:hidden">
        <RecipePrint
          recipe={recipe}
          className="h-12 w-12 rounded-full bg-theme-gray-100"
          iconClassName="fill-theme-primary transition-colors duration-300 hover:fill-theme-secondary"
        />
        <RecipeDownload
          recipe={recipe}
          className="h-12 w-12 rounded-full bg-theme-gray-100"
          iconClassName="fill-theme-primary transition-colors duration-300 hover:fill-theme-secondary"
        />
        <div className="hidden items-center gap-8 md:flex">
          <FacebookShareButton
            className="rounded-full !bg-theme-primary hover:!bg-facebook-blue"
            onClick={() => {
              gtag.event({
                action: TRACKING_EVENTS.facebook_share_click,
                category: TRACKING_CATEGORIES.interaction,
                label: getTitle(recipe.title ?? ""),
              });
              logStatsig(TRACKING_EVENTS.facebook_share_click);
              ClickServer.track(TRACKING_EVENTS.facebook_share_click);
            }}
            url={getFrontendSharingURL_App(pathName ?? "")}
          >
            <FacebookIconSocial className="h-12 w-12 scale-105 cursor-pointer fill-theme-gray-100" />
          </FacebookShareButton>

          <PinterestShareButton
            className="rounded-full !bg-theme-primary hover:!bg-pinterest-red"
            onClick={() => {
              gtag.event({
                action: TRACKING_EVENTS.pinterest_share_click,
                category: TRACKING_CATEGORIES.interaction,
                label: getTitle(recipe.title ?? ""),
              });
              logStatsig(TRACKING_EVENTS.pinterest_share_click);
              ClickServer.track(TRACKING_EVENTS.pinterest_share_click);
            }}
            media={getRecipeImageURL(recipe)}
            description={getPlaintextDescription(recipe)}
            url={getFrontendSharingURL_App(pathName ?? "")}
          >
            <PinterestIconSocial className="h-12 w-12 scale-105 cursor-pointer fill-theme-gray-100" />
          </PinterestShareButton>
        </div>

        <button
          className="inline-flex h-12 cursor-pointer items-center gap-2 rounded-full bg-theme-gray-100 px-4 py-2 font-extrabold text-theme-primary transition-colors duration-300 hover:text-theme-secondary md:hidden"
          onClick={async () => {
            gtag.event({
              action: TRACKING_EVENTS.sharesheet_button_click,
              category: TRACKING_CATEGORIES.interaction,
              label: recipe.title,
            });
            logStatsig(TRACKING_EVENTS.sharesheet_button_click);
            ClickServer.track(TRACKING_EVENTS.sharesheet_button_click);
            if (navigator.share) {
              await navigator
                .share({
                  title: recipe.title,
                  text: `Check out this recipe I love from ${brand.name}!`,
                  url: getFrontendSharingURL_App(pathName ?? ""),
                })
                .then((result) => {
                  log("Successfully shared");
                  log({ result: result });
                  gtag.event({
                    action: TRACKING_EVENTS.sharesheet_success,
                    category: TRACKING_CATEGORIES.interaction,
                    label: recipe.title,
                  });
                  logStatsig(TRACKING_EVENTS.sharesheet_success);
                  ClickServer.track(TRACKING_EVENTS.sharesheet_success);
                })
                .catch((error) => {
                  log("Error sharing");
                  log({ error: error });
                  if (error.message === "Abort due to cancellation of share.") {
                    gtag.event({
                      action: TRACKING_EVENTS.sharesheet_abort,
                      category: TRACKING_CATEGORIES.interaction,
                      label: recipe.title,
                    });
                    logStatsig(TRACKING_EVENTS.sharesheet_abort);
                    ClickServer.track(TRACKING_EVENTS.sharesheet_abort);
                    log("Share aborted");
                  } else {
                    gtag.event({
                      action: TRACKING_EVENTS.sharesheet_failure,
                      category: TRACKING_CATEGORIES.interaction,
                      label: recipe.title,
                    });
                    logStatsig(TRACKING_EVENTS.sharesheet_failure);
                    ClickServer.track(TRACKING_EVENTS.sharesheet_failure);
                    log("Share failed");
                  }
                });
            }
          }}
        >
          <ArrowUpOnSquareIcon className="w-6" />
          Share
        </button>
      </div>
    </>
  );
}
